import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  Box,
  Checkbox,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import { spacing } from "@mui/system";
import PDFViewerModal from "../../../pages/PDFViewer";
import { ArrowDropDown, Delete, Download, Search } from "@mui/icons-material";
import { Formik } from "formik";
import useAuth from "../../../hooks/useAuth";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import {
  createInvoice,
  deleteInvoice,
  deleteInvoicesBatch,
  fetchInvoices,
  generateInvoicesFromUnitList,
  openInvoiceForm,
  patchInvoice,
  setInvoice,
  updateInvoice,
  updateInvoicesBatchPaid,
  updateInvoicesBatchReminder,
  updateInvoicesBatchSent,
} from "../../../redux/slices/invoice";
import { format } from "date-fns";
import { baseUrl } from "../../../config";
import { fetchAssociation } from "../../../redux/slices/association";
import CustomSnackbar from "../SnackBar";
import { hideSnackbar } from "../../../redux/slices/ui";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Button = styled(MuiButton)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

function InvoiceForm() {
  const dispatch = useAppDispatch();
  const { invoice } = useAppSelector((state) => state.invoice);
  const { currentUser } = useAuth();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const handleSubmit = async (
    values: any,
    { resetForm, setSubmitting }: any
  ) => {
    if (invoice?.id) {
      dispatch(updateInvoice(invoice.id, values));
    } else {
      dispatch(createInvoice(values, associationId));
    }
    resetForm();
    setSubmitting(false);
    dispatch(openInvoiceForm(false));
  };

  return (
    <Formik
      initialValues={{
        invoiceNumber: invoice?.invoiceNumber || "",
        dueDate: invoice?.dueDate || "",
        total: invoice?.total || "",
      }}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, handleChange, handleSubmit }) => (
        <Card mb={6}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Lasku
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={6}>
                <Grid item md={4}>
                  <TextField
                    label="Laskun numero"
                    name="invoiceNumber"
                    value={values.invoiceNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={4}>
                  <TextField
                    label="Eräpäivä"
                    name="dueDate"
                    type="date"
                    value={values.dueDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={4}>
                  <TextField
                    label="Summa (€)"
                    name="total"
                    value={values.total}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    my={2}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                mt={3}
                mr={2}
              >
                Tallenna
              </Button>
              <Button
                variant="contained"
                color="error"
                mt={3}
                onClick={() => dispatch(openInvoiceForm(false))}
              >
                Peruuta
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </Formik>
  );
}

function InvoiceList({
  handleCreateInvoicesFromUnits,
  handleEditInvoice,
  handleShowInvoice,
  handleDelete,
  loading,
}: any) {
  const dispatch = useAppDispatch();
  const { association, status: associationStatus } = useAppSelector(
    (state) => state.association
  );
  const { invoices, status: invoiceStatus } = useAppSelector(
    (state) => state.invoice
  );
  const { currentUser } = useAuth();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const isReady = associationStatus === "idle" && invoiceStatus === "idle";

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const allSelected =
    invoices?.length > 0 && selectedIds.length === invoices.length;

  const handleDownload = async (rowFile: any) => {
    const filename = rowFile.file.filename;
    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create a link element, set the URL and click it
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Cleanup: remove the link and revoke the object URL
      link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev: any) =>
      prev.includes(id) ? prev.filter((i: any) => i !== id) : [...prev, id]
    );
  };

  const handleBulkSend = async (ids: number[]) => {
    try {
      await dispatch(updateInvoicesBatchSent({ ids, associationId })).unwrap();
      // No need to show UI here; ui slice already did on fulfilled/rejected.
    } catch {
      // optional: nothing; error snackbar already shown by matcher
    }
  };

  const handleBulkReminder = async (ids: number[]) => {
    try {
      await dispatch(
        updateInvoicesBatchReminder({
          ids,
          associationId,
        })
      ).unwrap();
    } catch {
      // optional
    }
  };

  const handleBulkMarkPaid = async (ids: number[]) => {
    try {
      await dispatch(
        updateInvoicesBatchPaid({
          ids,
          associationId,
        })
      ).unwrap();
    } catch {
      // optional
    }
  };

  const handleBulkDelete = (ids: number[]) => {
    if (window.confirm(`Poistetaanko ${ids.length} laskua?`)) {
      dispatch(deleteInvoicesBatch(ids, associationId));
      setSelectedIds([]);
    }
  };

  const toggleSelectAll = () => {
    if (!invoices) return;
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map((i: any) => i.id));
    }
  };

  const [actionMenuAnchorEl, setActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const isActionMenuOpen = Boolean(actionMenuAnchorEl);

  const openActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null);
  };

  type InvoiceStatus =
    | "queued"
    | "pdf_generating"
    | "draft"
    | "failed"
    | "sent"
    | "reminded"
    | "paid";

  const getInvoiceStatusInfo = (
    status: InvoiceStatus
  ): {
    label: string;
    color: ChipProps["color"];
    variant?: "filled" | "outlined";
  } => {
    switch (status) {
      case "queued":
        return { label: "Jonossa", color: "default", variant: "outlined" };
      case "pdf_generating":
        return { label: "Luodaan", color: "info", variant: "outlined" };
      case "draft":
        return { label: "Luotu", color: "success", variant: "filled" };
      case "failed":
        return { label: "Epäonnistui", color: "error", variant: "filled" };
      case "sent":
        return { label: "Lähetetty", color: "primary", variant: "filled" };
      case "reminded":
        return {
          label: "Muistutus lähetetty",
          color: "warning",
          variant: "filled",
        };
      case "paid":
        return { label: "Maksettu", color: "success", variant: "filled" };
      default:
        return { label: "Tuntematon", color: "default", variant: "outlined" };
    }
  };

  useEffect(() => {
    dispatch(fetchAssociation(associationId));
    return () => {
      dispatch(openInvoiceForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <Card mb={6}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Laskutus
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleCreateInvoicesFromUnits()}
              disabled={
                loading ||
                association?.iban === null ||
                association?.bic === null
              }
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Luo maksuunpanoluettelosta"
              )}
            </Button>
            {/* <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleEditInvoice(undefined, true)}
            >
              Lisää lasku
            </Button> */}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: selectedIds.length > 0 ? "#e3f2fd" : "#f5f5f5",
            borderRadius: 1,
            px: 2,
            py: 1.5,
            mt: 1,
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {selectedIds.length > 0
              ? `${selectedIds.length} laskua valittuna`
              : "Valitse laskuja käyttääksesi massatoimintoja"}
          </Typography>

          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={openActionMenu}
              disabled={selectedIds.length === 0}
              endIcon={<ArrowDropDown />}
            >
              Toiminnot
            </Button>

            <Menu
              anchorEl={actionMenuAnchorEl}
              open={isActionMenuOpen}
              onClose={closeActionMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  handleBulkSend(selectedIds);
                  closeActionMenu();
                }}
              >
                Lähetä lasku
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkReminder(selectedIds);
                  closeActionMenu();
                }}
              >
                Lähetä muistutuslasku
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkMarkPaid(selectedIds);
                  closeActionMenu();
                }}
              >
                Merkitse maksetuksi
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkDelete(selectedIds);
                  closeActionMenu();
                }}
              >
                Poista
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < invoices.length
                  }
                />
              </TableCell>
              <TableCell>Laskun numero</TableCell>
              <TableCell>Nimi</TableCell>
              <TableCell>Saaja</TableCell>
              <TableCell>Luontipäivä</TableCell>
              <TableCell>Eräpäivä</TableCell>
              <TableCell>Summa (€)</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isReady ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Ei laskuja
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </TableCell>
                  <TableCell>{row.invoiceNumber}</TableCell>
                  <TableCell>
                    {row.file?.originalname ?? "PDF puuttuu"}
                  </TableCell>
                  <TableCell>
                    {row.user.firstName + " " + row.user.lastName ||
                      "Ei tiedossa"}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.issueDate), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.dueDate), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>{row.total.toFixed(2)}</TableCell>
                  <TableCell>
                    {(() => {
                      const { label, color, variant } = getInvoiceStatusInfo(
                        row.status as InvoiceStatus
                      );
                      return (
                        <Chip
                          size="small"
                          label={label}
                          color={color}
                          variant={variant}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Download
                      color={!row.file?.filename ? "disabled" : "inherit"}
                      style={{
                        cursor: !row.file?.filename ? "not-allowed" : "pointer",
                      }}
                      onClick={
                        !row.file?.filename
                          ? undefined
                          : () => handleDownload(row)
                      }
                    />
                    <Search
                      color={!row.file?.filename ? "disabled" : "inherit"}
                      style={{
                        cursor: !row.file?.filename ? "not-allowed" : "pointer",
                      }}
                      onClick={
                        !row.file?.filename
                          ? undefined
                          : () => handleShowInvoice(row, true)
                      }
                    />
                    {/* <Edit
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEditInvoice(row, true)}
                  /> */}
                    <Delete
                      color={!row.file?.filename ? "disabled" : "inherit"}
                      style={{
                        cursor: !row.file?.filename ? "not-allowed" : "pointer",
                      }}
                      onClick={() => handleBulkDelete([row.id])}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Invoice() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { snackbar } = useAppSelector((s) => s.ui);
  const { open } = useAppSelector((state) => state.invoice);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [loading, setLoading] = useState(false);
  const esRef = useRef<EventSource | null>(null);
  const finishedIdsRef = useRef<Set<number>>(new Set());
  const expectedCountRef = useRef<number>(0);

  const handleEditInvoice = (invoice: any, isOpen: boolean) => {
    dispatch(setInvoice(invoice));
    dispatch(openInvoiceForm(isOpen));
  };

  const handleShowInvoice = (invoice: any, isOpen: boolean) => {
    handleShowInvoiceModal(invoice, isOpen);
  };

  const handleDelete = (invoice: any) => {
    dispatch(deleteInvoice(invoice.id, associationId));
  };

  const handleShowInvoiceModal = (invoice: any, isOpen: boolean) => {
    const filename = invoice.file?.filename;
    if (!filename) return;

    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(isOpen);
  };

  const resetBatchCounters = (recipients: number) => {
    finishedIdsRef.current = new Set();
    expectedCountRef.current = recipients;
  };

  const maybeFinalize = async (es: EventSource) => {
    if (finishedIdsRef.current.size >= expectedCountRef.current) {
      await dispatch(fetchInvoices(associationId));
      es.close();
      if (esRef.current === es) esRef.current = null;
      setLoading(false);
    }
  };

  const openInvoiceStream = async (
    batchId: string,
    streamUrl: string,
    recipients: number
  ) => {
    resetBatchCounters(recipients);

    // Load snapshot first
    await dispatch(fetchInvoices(associationId));

    // Open SSE with auth token
    if (esRef.current) esRef.current.close();

    // Get auth token from localStorage
    const token = localStorage.getItem("accessToken");
    if (!token) {
      console.error("No auth token found");
      setLoading(false);
      return;
    }

    const baseStreamUrl = streamUrl.startsWith("http")
      ? streamUrl
      : `${baseUrl}${streamUrl}`;
    const url = `${baseStreamUrl}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("open", () => {});

    // Live patch for progress
    es.addEventListener("progress", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.invoiceId) return;

      // Patch UI
      dispatch(
        patchInvoice({
          invoiceId: msg.invoiceId,
          updates: {
            status: msg.status,
            pct: msg.pct,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );
    });

    // Count completion
    es.addEventListener("completed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.invoiceId) return;

      // Patch to generated
      dispatch(
        patchInvoice({
          invoiceId: msg.invoiceId,
          updates: {
            status: msg.status ?? "draft",
            pct: 100,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.invoiceId)) {
        finishedIdsRef.current.add(msg.invoiceId);
        maybeFinalize(es);
      }
    });

    es.addEventListener("failed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.invoiceId) return;

      // Patch to failed
      dispatch(
        patchInvoice({
          invoiceId: msg.invoiceId,
          updates: {
            status: "failed",
            lastError: msg.reason ?? msg.lastError ?? "Tuntematon virhe",
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.invoiceId)) {
        finishedIdsRef.current.add(msg.invoiceId);
        maybeFinalize(es);
      }
    });

    es.addEventListener("ping", () => {});
    es.addEventListener("error", () => {
      console.error("SSE error");
    });
  };

  const handleCreateInvoicesFromUnits = async () => {
    setLoading(true);
    try {
      const result = await dispatch(
        generateInvoicesFromUnitList(associationId)
      );

      const { batchId, recipients, streamUrl } = result;

      await openInvoiceStream(batchId, streamUrl, recipients);
    } catch (e: any) {
      console.error(e);
      setLoading(false);
      alert("Laskujen luonti epäonnistui");
    }
  };

  useEffect(() => {
    dispatch(fetchInvoices(associationId));
    return () => {
      dispatch(openInvoiceForm(false));
      if (esRef.current) esRef.current.close();
    };
  }, [dispatch, associationId]);

  return (
    <>
      <Helmet title="Laskut" />
      <Typography variant="h3" gutterBottom>
        Laskutus
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Link component={NavLink} to="/">
          Talous
        </Link>
        <Typography>Laskutus</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      {open ? (
        <InvoiceForm />
      ) : (
        <InvoiceList
          handleCreateInvoicesFromUnits={handleCreateInvoicesFromUnits}
          handleShowInvoice={handleShowInvoice}
          handleEditInvoice={handleEditInvoice}
          handleDelete={handleDelete}
          loading={loading}
        />
      )}

      <CustomSnackbar
        open={Boolean(snackbar?.open)}
        message={snackbar?.message ?? ""}
        severity={snackbar?.severity ?? "info"}
        progress={100}
        duration={snackbar?.duration ?? 4000}
        onClose={() => dispatch(hideSnackbar())}
      />
    </>
  );
}

export default Invoice;
